/**
 * The ONE translation from the grouper's wire-shaped `ApprovalPart` into the
 * `ApprovalCardData` an `<ApprovalCard>` consumes.
 *
 * **Why this lives in its own module.** Two render paths mount the same card:
 * the default `CopilotMessages` markup (`primitives/messages.tsx`) and the
 * batteries-included widget (`copilot/panel-content.tsx`), which supplies its
 * own `renderApprovalCard`. Each used to build this object inline, and the
 * widget's copy carried a comment asking whoever edited one to remember the
 * other. That comment did not survive first contact: ATRU-01 added `execution`
 * to the `messages.tsx` copy only, so `Applied ✓` / `Execution failed` could
 * never render on the widget — the path every track frontend actually mounts —
 * while the unit tests of the other path stayed green and a real approval sat
 * on screen saying `Approved` after its write had already succeeded.
 *
 * So the mapping is single-homed here and BOTH paths import it. A field added
 * to `ApprovalPart` now reaches every card by construction rather than by
 * anybody noticing. `approval-card-parity.test.tsx` renders the same approval
 * through both paths and requires identical chips, so a future second copy
 * fails a test instead of shipping.
 *
 * Keep this module free of React and of any import back into `messages.tsx` or
 * `panel-content.tsx`: it sits BELOW both of them in the import graph, which is
 * what lets both depend on it without a cycle.
 */
export function approvalPartToCardData(approval) {
    return {
        approvalId: approval.approvalId,
        toolName: approval.toolName,
        toolCallId: approval.toolCallId,
        args: approval.args,
        payloadHash: approval.payloadHash,
        expiresAt: approval.expiresAt,
        summary: approval.summary ?? null,
        references: approval.references,
        state: approval.state,
        // ATRU-01 / COP-134 D1 — execution truth beside the decision. Omitted when
        // there is nothing to show; ABSENT is never "failed".
        ...(approval.execution ? { execution: approval.execution } : {}),
        reason: approval.reason ?? null,
        errorCode: approval.errorCode,
        errorMessage: approval.errorMessage,
        // COP-093 — consent-scope affordance hints: the "Always allow <tool>"
        // choice is gated on `trackId`, and `alreadyAlwaysGranted` drives the
        // "you always allow this — Undo" state.
        ...(approval.trackId !== undefined ? { trackId: approval.trackId } : {}),
        ...(approval.alreadyAlwaysGranted ? { alreadyAlwaysGranted: true } : {}),
        // COP-160 SCHED-20a — the card offers the schedule scope.
        ...(approval.scheduledExecution ? { scheduledExecution: true } : {}),
    };
}
//# sourceMappingURL=approval-card-data.js.map